/**
 * Clock-time formatting against the session's AHP-provided UTC offset.
 *
 * Sessions carry no IANA timezone — AHP embeds a UTC offset (e.g. "+08:00") in
 * its datetimes, preserved on the session row as `timezone`. Lambdas run with a
 * UTC clock, so any push/copy that renders a session-local wall-clock time MUST
 * shift by this offset first: a bare `toLocaleTimeString` on a lambda renders
 * UTC wall-clock ("Leave by 6:45 am" for a 2:45 pm Perth session).
 */

/** Parse an AHP UTC-offset label like "+08:00" / "-09:30" into milliseconds. */
export function parseUtcOffsetMs(offset: string | null | undefined): number | null {
  if (!offset) return null
  const m = /^([+-])(\d{2}):(\d{2})$/.exec(offset.trim())
  if (!m) return null
  const sign = m[1] === '-' ? -1 : 1
  return sign * (Number(m[2]) * 60 + Number(m[3])) * 60_000
}

/**
 * Format `ms` as a short clock time (e.g. "2:45 pm") in the given UTC offset.
 * Falls back to the runtime's local zone when the offset is missing or
 * unparseable — correct on devices, UTC on lambdas, so server-side callers
 * should always pass the session's stored offset.
 */
export function formatClockTime(ms: number, utcOffset?: string | null): string {
  const offsetMs = parseUtcOffsetMs(utcOffset)
  if (offsetMs == null) {
    return new Date(ms).toLocaleTimeString('en-AU', { hour: 'numeric', minute: '2-digit' })
  }
  return new Date(ms + offsetMs).toLocaleTimeString('en-AU', {
    hour: 'numeric',
    minute: '2-digit',
    timeZone: 'UTC',
  })
}

/**
 * Clock range compressing a shared meridiem: "9:00–10:00 am", "11:30 am–1:00
 * pm". Same session-offset rules as {@link formatClockTime}.
 */
export function formatClockTimeRange(
  startMs: number,
  endMs: number,
  utcOffset?: string | null,
): string {
  const start = formatClockTime(startMs, utcOffset)
  const end = formatClockTime(endMs, utcOffset)
  const meridiem = /\s([ap]m)$/i
  const startM = meridiem.exec(start)?.[1]
  const endM = meridiem.exec(end)?.[1]
  if (startM && endM && startM === endM) {
    return `${start.replace(meridiem, '')}–${end}`
  }
  return `${start}–${end}`
}

/** Session-local calendar day key for same-day comparisons. */
function calendarDayKey(ms: number, offsetMs: number | null): string {
  const d = offsetMs == null ? new Date(ms) : new Date(ms + offsetMs)
  return offsetMs == null
    ? `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`
    : `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}`
}

/** Short session-local date, e.g. "Tue 7 Oct" (no year — chat-tile copy). */
export function formatShortDate(ms: number, utcOffset?: string | null): string {
  const offsetMs = parseUtcOffsetMs(utcOffset)
  if (offsetMs == null) {
    return new Date(ms).toLocaleDateString('en-AU', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    })
  }
  return new Date(ms + offsetMs).toLocaleDateString('en-AU', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })
}

const DAY_MS = 24 * 60 * 60 * 1000

/**
 * Date label for a session list row: "Today", "Yesterday", "Tomorrow", or
 * the short date ("Tue 7 Oct"). Compared in the session's offset so a late
 * Perth session is still "Today" after UTC midnight. Used on the Recent
 * list, which otherwise only showed clock times.
 */
export function formatSessionListDate(
  ms: number,
  utcOffset?: string | null,
  now: number = Date.now(),
): string {
  const offsetMs = parseUtcOffsetMs(utcOffset)
  const key = calendarDayKey(ms, offsetMs)
  if (key === calendarDayKey(now, offsetMs)) return 'Today'
  if (key === calendarDayKey(now - DAY_MS, offsetMs)) return 'Yesterday'
  if (key === calendarDayKey(now + DAY_MS, offsetMs)) return 'Tomorrow'
  return formatShortDate(ms, utcOffset)
}

/**
 * Relative day phrase for chat/push copy: "today", "tomorrow", or
 * "on Tue 7 Oct". Compared in the SESSION's offset (a 00:30 session is
 * "tomorrow" in Perth even while the UTC clock still says yesterday). Copy is
 * re-derived at render time, so "today" ages into the absolute date naturally.
 */
export function formatSessionDayPhrase(
  ms: number,
  utcOffset?: string | null,
  now: number = Date.now(),
): string {
  const offsetMs = parseUtcOffsetMs(utcOffset)
  const key = calendarDayKey(ms, offsetMs)
  if (key === calendarDayKey(now, offsetMs)) return 'today'
  if (key === calendarDayKey(now + DAY_MS, offsetMs)) return 'tomorrow'
  return `on ${formatShortDate(ms, utcOffset)}`
}
